import type { Database } from 'sql.js';
import { lookupBonoNombreById } from '@core/infrastructure/driven/sqljs/sqlJsLookupBonoNombre.ts';
import { captureTipoBonoTextFromSqlRow } from '@core/infrastructure/driven/sqljs/sqlJsLookupTipoBonoNombre.ts';
import {
  publicFileUrl,
  queryOne,
  rowFirstKeyColumn as pepCol,
  type SqlJsBind,
  sqlIntOrZero,
  sqlPositiveIntId,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type {
  ParticipanteResumen,
  PuntoEntregaListItem,
  PuntoEntregaParticipanteFormPayload,
} from '../../../domain/puntoEntregaModel.ts';
import { mapParticipanteSqlRowToResumen } from '../../../../shared/participante/infrastructure/mapParticipanteSqlRowToResumen.ts';
import { resolveAcudienteRow } from './acudienteResolve.ts';

type SqlBind = SqlJsBind;

/** Participant row (`participantes` table) to summary DTO. */
export function participanteRowToResumen(p: Record<string, SqlBind>): ParticipanteResumen {
  return mapParticipanteSqlRowToResumen(p);
}

export function mapPuntoEntregaListRow(r: Record<string, SqlBind>): PuntoEntregaListItem {
  return {
    id: Number(r.id),
    publicId: String(r.public_id ?? ''),
    entregaId: Number(r.entrega_id),
    fechaHora: String(r.fecha_hora ?? ''),
    esRemanente: Number(r.es_remanente) === 1,
    entregaNombre: String(r.entrega_nombre ?? ''),
    lugarNombre: String(r.lugar_nombre ?? ''),
    comunaNombre: String(r.comuna_nombre ?? ''),
    barrioNombre: String(r.barrio_nombre ?? ''),
    totalParticipantes: Number(r.total_participantes ?? 0),
    totalCapacitados: Number(r.total_capacitados ?? 0),
  };
}

export function mapPepToForm(
  db: Database,
  filesPublicPrefix: string,
  pep: Record<string, SqlBind>,
): PuntoEntregaParticipanteFormPayload {
  const esAcud = Number(pepCol(pep, 'es_acudiente', 'ES_ACUDIENTE') ?? 0) === 1;
  const acudienteRow = resolveAcudienteRow(db, pep);
  let acudiente: PuntoEntregaParticipanteFormPayload['acudiente'];
  if (esAcud && acudienteRow) {
    const pepDoc = (k: string) => pepCol(pep, k, k.toUpperCase());
    const pick = (col: string, pepFallback: SqlBind) =>
      publicFileUrl(
        acudienteRow[col] != null && String(acudienteRow[col]).trim() !== '' ? acudienteRow[col] : pepFallback,
        filesPublicPrefix,
      );
    acudiente = {
      nombre: String(acudienteRow.nombre ?? ''),
      parentescoId: sqlPositiveIntId(pepCol(acudienteRow, 'parentesco_id', 'PARENTESCO_ID')) ?? 0,
      documentoFrontal: pick('documento_beneficiario_frontal_path', pepDoc('documento_frontal') ?? ''),
      documentoReverso: pick('documento_beneficiario_reverso_path', pepDoc('documento_reverso') ?? ''),
      documentoAutorizadoFrontal: pick('documento_autorizado_frontal_path', pepDoc('documento_autorizado_frontal') ?? ''),
      documentoAutorizadoReverso: pick(
        'documento_autorizado_reverso_path',
        pepDoc('documento_autorizado_reverso') ?? '',
      ),
      autorizado: pick('autorizado_path', pepDoc('autorizado') ?? ''),
    };
  } else if (esAcud && acudienteRow == null) {
    const docFromPep = (k: string) =>
      publicFileUrl(String(pepCol(pep, k, k.toUpperCase()) ?? ''), filesPublicPrefix);
    acudiente = {
      nombre: '',
      parentescoId: 0,
      documentoFrontal: docFromPep('documento_frontal'),
      documentoReverso: docFromPep('documento_reverso'),
      documentoAutorizadoFrontal: docFromPep('documento_autorizado_frontal'),
      documentoAutorizadoReverso: docFromPep('documento_autorizado_reverso'),
      autorizado: docFromPep('autorizado'),
    };
  }
  const participanteId = sqlIntOrZero(pepCol(pep, 'participante_id', 'PARTICIPANTE_ID'));
  const participanteRow =
    participanteId > 0
      ? queryOne(db, 'SELECT nombre, telefono, celular FROM participantes WHERE id = ?', [participanteId])
      : null;
  const bonoIdVal = sqlIntOrZero(pepCol(pep, 'bono_id', 'BONO_ID'));
  const bonoCaption = lookupBonoNombreById(db, bonoIdVal) ?? '';
  return {
    participanteId,
    nombreParticipante: String(participanteRow?.nombre ?? ''),
    telefono: String(participanteRow?.telefono ?? ''),
    celular: String(participanteRow?.celular ?? ''),
    presentaAcudienteAutorizado: esAcud,
    acudiente,
    documentoFrontal: publicFileUrl(pepCol(pep, 'documento_frontal', 'DOCUMENTO_FRONTAL') ?? '', filesPublicPrefix),
    documentoReverso: publicFileUrl(pepCol(pep, 'documento_reverso', 'DOCUMENTO_REVERSO') ?? '', filesPublicPrefix),
    documentoAutorizadoFrontal: publicFileUrl(
      pepCol(pep, 'documento_autorizado_frontal', 'DOCUMENTO_AUTORIZADO_FRONTAL') ?? '',
      filesPublicPrefix,
    ),
    documentoAutorizadoReverso: publicFileUrl(
      pepCol(pep, 'documento_autorizado_reverso', 'DOCUMENTO_AUTORIZADO_REVERSO') ?? '',
      filesPublicPrefix,
    ),
    autorizado: publicFileUrl(pepCol(pep, 'autorizado', 'AUTORIZADO') ?? '', filesPublicPrefix),
    asistencia: Number(pepCol(pep, 'asistencia', 'ASISTENCIA') ?? 0) === 1,
    capacitado: Number(pepCol(pep, 'capacitado', 'CAPACITADO') ?? 0) === 1,
    acta: publicFileUrl(pepCol(pep, 'acta', 'ACTA') ?? '', filesPublicPrefix),
    entregaBolsa: Number(pepCol(pep, 'entrega_bolsa', 'ENTREGA_BOLSA') ?? 0) === 1,
    bonoId: bonoIdVal,
    bono: bonoCaption,
    tipoBono: captureTipoBonoTextFromSqlRow(db, pep),
    firmaBase64: publicFileUrl(pepCol(pep, 'firma_base64', 'FIRMA_BASE64') ?? '', filesPublicPrefix),
    observaciones: String(pepCol(pep, 'observaciones', 'OBSERVACIONES') ?? ''),
    registroFotografico: publicFileUrl(pepCol(pep, 'registro_fotografico', 'REGISTRO_FOTOGRAFICO') ?? '', filesPublicPrefix),
    politicaTratamientos: Number(pepCol(pep, 'politica_tratamientos', 'POLITICA_TRATAMIENTOS') ?? 0) === 1,
  };
}
